import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, Rocket, Ticket } from 'lucide-react';
import type { RestartOffer } from '@pnl/shared';

interface Props {
  offer: RestartOffer;
  accountLabel: string;
}

// Identidade visual do restart (briefing): card de gradiente navy sólido, texto
// branco e botão branco. O app não tem token navy-* → hex direto.
const GRAD = 'bg-gradient-to-br from-[#0D1580] to-[#1E6FFF] rounded-xl p-5';
const BTN =
  'bg-white text-[#0D1580] hover:bg-white/90 inline-flex shrink-0 items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors';

/**
 * Banner pós-eliminação no topo do dashboard. Modos:
 * - pendingActivation: restart solicitado, aguardando aprovação do admin (verde).
 * - failedRestart: já refez e estourou de novo → novo teste com 20% (link planos).
 * - selfService: restart incluso (campanha) → solicita em "Minhas contas".
 * - pago (default): checkout de restart DENTRO do app (/pagar-restart) + cupom.
 *
 * Identidade azul (gradiente navy) nos modos de restart/novo-teste; verde só na
 * confirmação de solicitação.
 */
export function RestartOfferBanner({ offer, accountLabel }: Props) {
  // 1) Restart solicitado — aguardando a equipe aprovar (reinicia do zero ao aprovar).
  if (offer.pendingActivation) {
    return (
      <div className="border-success-500/40 bg-success-500/10 rounded-xl border p-5">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="bg-success-500/20 text-success-300 grid h-10 w-10 shrink-0 place-items-center rounded-lg">
              <CheckCircle2 size={18} />
            </div>
            <div>
              <h3 className="text-success-200 text-base font-semibold">Restart solicitado!</h3>
              <p className="text-ink-300 mt-1 text-sm leading-relaxed">
                Recebemos sua solicitação de restart da conta{' '}
                <strong className="text-ink-100">{accountLabel}</strong>. Nossa equipe vai liberar e
                ela reinicia do zero — você será avisado.
              </p>
            </div>
          </div>
          <Link
            to="/accounts"
            className="border-success-500/40 text-success-200 inline-flex shrink-0 items-center justify-center gap-2 rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-white/5"
          >
            <Clock size={15} /> Acompanhar
          </Link>
        </div>
      </div>
    );
  }

  // 2) Já refez o restart e estourou de novo → novo teste. Texto editável pelo admin
  // (o cupom já vem embutido no texto via [CUPOM]).
  if (offer.failedRestart) {
    return (
      <div className={GRAD}>
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white/15 text-white">
              <Rocket size={18} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">
                Até os melhores Traders tomam stop!
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-white/80">
                A conta <strong className="text-white">{accountLabel}</strong> foi encerrada.{' '}
                {offer.bannerText}
              </p>
            </div>
          </div>
          <a
            href={offer.newTestUrl ?? 'https://pnlglobalb3.com.br/planos'}
            target="_blank"
            rel="noreferrer"
            className={BTN}
          >
            <Rocket size={15} /> Iniciar novo Challenge
          </a>
        </div>
      </div>
    );
  }

  // 3) Restart incluso (campanha) — solicita e ativa (não reinicia na hora).
  if (offer.selfService) {
    return (
      <div className={GRAD}>
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white/15 text-white">
              <Rocket size={18} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">Você tem um restart incluso!</h3>
              <p className="mt-1 text-sm leading-relaxed text-white/80">
                A conta <strong className="text-white">{accountLabel}</strong> foi encerrada — mas
                seu plano inclui um restart. Solicite em "Minhas Contas"; nossa equipe libera e a
                conta reinicia do zero, sem pagar nada.
              </p>
            </div>
          </div>
          <Link to="/accounts" className={BTN}>
            <Rocket size={15} /> Fazer restart
          </Link>
        </div>
      </div>
    );
  }

  // 4) Restart pago (1ª eliminação) — checkout interno.
  return (
    <div className={GRAD}>
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white/15 text-white">
            <Rocket size={18} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-white">
              Até os melhores Traders tomam stop!
            </h3>
            <p className="mt-1 text-sm leading-relaxed text-white/80">
              A conta <strong className="text-white">{accountLabel}</strong> foi encerrada. Recomece
              sua avaliação agora fazendo o Restart do seu plano PnL.
            </p>
            {offer.couponCode && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-white/90">
                <Ticket size={14} className="text-white" /> Use o cupom{' '}
                <span className="rounded bg-white/20 px-1.5 py-0.5 font-mono font-semibold text-white">
                  {offer.couponCode}
                </span>{' '}
                de desconto.
              </p>
            )}
          </div>
        </div>
        {offer.paymentAvailable ? (
          <Link to={offer.checkoutUrl} className={BTN}>
            <Rocket size={15} /> Pagar restart
          </Link>
        ) : (
          <span className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-white/30 px-4 py-2.5 text-sm font-semibold text-white/70">
            <Rocket size={15} /> Restart em breve
          </span>
        )}
      </div>
    </div>
  );
}
